import React, { useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import HighchartsInterface from '@veneer/highcharts-interface';
import ChartProvider from '@veneer/core/dist/scripts/chart/chart_provider';
import Chart from '@veneer/core/dist/scripts/chart/chart';
import ErrorWidget from '../../ErrorWidget';
import LoaderWidget from '../../LoaderWidget';
import { mediaQueries } from '../../../helpers/mixins/mediaQueries';
import useMediaQuery from '../../../hooks/useMediaQuery';
import { generalTooltip } from '../utils';
import { CustomChart, ErrorContainer, LoaderContainer } from './styles';
import NoDataYet from '../../NoDataYet';

const GenericChart = ({
  'data-testid': dataTestId,
  error,
  loading,
  onRetry,
  options,
  tooltip,
  noData,
  i18n,
}) => {
  const isMobile = useMediaQuery(mediaQueries.xs);
  const maxXAxis = options.xAxis.categories.length - 1;
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);

  useEffect(() => {
    if (!chartContainerRef.current || !chartRef.current) {
      return () => {};
    }

    const resizeObserver = new ResizeObserver(() => {
      if (chartRef.current) {
        chartRef.current.chart.reflow();
      }
    });

    resizeObserver.observe(chartContainerRef.current);
    return () => resizeObserver.disconnect();
  }, [chartContainerRef, chartRef]);

  const chartOptions = useMemo(() => ({
    chart: {
      height: 250,
      events: {
        redraw() {
          const chart = this;
          if (!isMobile && (chart.xAxis[0].min !== 0 || chart.xAxis[0].max !== maxXAxis)) {
            chart.xAxis[0].setExtremes(0, maxXAxis);
          }

          if (isMobile && (chart.xAxis[0].min === 0 && chart.xAxis[0].max === maxXAxis)) {
            chart.xAxis[0].setExtremes(0, 4);
          }
        },
      },
      scrollablePlotArea: {
        scrollPositionX: 0,
      },
    },
    title: {
      text: '',
    },
    legend: {
      squareSymbol: true,
      symbolRadius: 3,
      symbolHeight: 16,
      ...(isMobile && {
        itemMarginTop: 8,
        margin: 4,
      }),
    },
  }), [isMobile, maxXAxis]);

  if (error) {
    return (
      <ErrorContainer>
        <ErrorWidget
          message={i18n.error}
          labelRetry={i18n.retry}
          onRetry={onRetry}
        />
      </ErrorContainer>
    );
  }

  if (loading) {
    return (
      <LoaderContainer>
        <LoaderWidget fullScreen />
      </LoaderContainer>
    );
  }

  if (noData) {
    return (
      <NoDataYet
        i18n={{
          title: i18n.noDataAvailable?.title,
          subTitle: i18n.noDataAvailable?.subTitle,
        }}
      />
    );
  }

  return (
    <ChartProvider chartInterface={HighchartsInterface}>
      <CustomChart data-testid={dataTestId} ref={chartContainerRef}>
        <Chart
          ref={chartRef}
          options={{
            ...chartOptions,
            ...options,
            legend: { ...chartOptions.legend, ...options.legend },
            tooltip: {
              ...generalTooltip,
              ...tooltip,
            },
          }}
        />
      </CustomChart>
    </ChartProvider>
  );
};

GenericChart.propTypes = {
  error: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  noData: PropTypes.bool,
  onRetry: PropTypes.func.isRequired,
  options: PropTypes.objectOf(PropTypes.any).isRequired,
  'data-testid': PropTypes.string,
  i18n: PropTypes.shape({
    retry: PropTypes.string.isRequired,
    error: PropTypes.string.isRequired,
    noDataAvailable: PropTypes.shape({
      title: PropTypes.string.isRequired,
      subTitle: PropTypes.string.isRequired,
    }),
  }).isRequired,
  tooltip: PropTypes.shape({
    shared: PropTypes.bool,
    followPointer: PropTypes.bool,
    formatter: PropTypes.func,
  }).isRequired,
};

GenericChart.defaultProps = {
  'data-testid': '',
  noData: false,
};

export default GenericChart;
